/**
 * Что приложение умеет посчитать — порт `TelemetryEvent.kt`.
 *
 * **Перечень закрытый и без единого строкового параметра.** Названия дел, клиентов
 * и документов, теги и заметки не могут попасть в статистику: для них здесь просто
 * нет места. В Kotlin это стережёт `enum`, здесь — тип `TelemetryEvent`, который
 * не принимает ничего, кроме этих имён.
 *
 * Имена метрик обязаны совпадать с Android и с белым списком `METRICS`
 * в `server/stats/service.py`: разошлись — метрика молча не запишется.
 * См. docs/TELEMETRY.md.
 */
export const TELEMETRY_EVENTS = {
  appOpened: 'app_opened',

  caseCreated: 'case_created',
  /** Открытие **существующего** дела — главный индикатор возврата, а не создание нового. */
  caseOpened: 'case_opened',

  documentScanned: 'document_scanned',
  documentFromCamera: 'document_from_camera',
  documentFromGallery: 'document_from_gallery',
  documentFromFiles: 'document_from_files',
  documentOpened: 'document_opened',
  documentRenamed: 'document_renamed',
  documentTyped: 'document_typed',
  documentReordered: 'document_reordered',
  documentFavorited: 'document_favorited',
  documentsShared: 'documents_shared',

  taskCreated: 'task_created',
  taskCompleted: 'task_completed',
  eventCreated: 'event_created',

  clientCreated: 'client_created',
  searchUsed: 'search_used',
  searchResultOpened: 'search_result_opened',

  /*
   * Падение приложения — **число, и только число**.
   *
   * Заведено 15.09.2026 по жалобе «люди не могут зайти, белый экран». Главный
   * неизвестный тогда был не «какая ошибка», а «сколько людей и на чём»: «люди»
   * может значить и двоих, и двадцать, а по снимку экрана это не считается.
   * Теперь в отчёте видно «за сутки 4 падения у 2 установок» — платформа, версия
   * системы и класс устройства лежат в `installs` и так.
   *
   * **Текста ошибки здесь нет и не будет**: он живёт на экране устройства, и в нём
   * может оказаться кусок названия дела ([D-61](docs/DECISIONS.md), решение владельца).
   * Место для строкового параметра в этом перечне не предусмотрено вовсе.
   *
   * Отчёт уходит не сразу: число пишется при падении, а улетает при следующем удачном
   * запуске. Приложение, которое не запускается совсем, о себе не сообщит — как
   * и незагрузка бандла, которой телеметрия не видит по построению (PITFALLS §22).
   */
  appCrashed: 'app_crashed',
} as const;

export type TelemetryEvent = keyof typeof TELEMETRY_EVENTS;

/**
 * Поля суточного среза — состояние на конец суток, а не события.
 *
 * Ровно ответ на вопрос «сколько дел ведёт, сколько документов». Одна строка в сутки
 * на установку.
 */
export interface TelemetrySnapshot {
  cases_active: number;
  cases_total: number;
  documents_total: number;
  tasks_open: number;
  tasks_overdue: number;
  clients_total: number;
  storage_mb: number;
}

/**
 * Имена полей среза. Срез и события лежат в одном хранилище под ключами «дата.имя»,
 * а на сервере это разные таблицы: одно — сколько раз сделали, другое — сколько всего есть.
 * Разделяет их отправка, и разделяет по этому списку.
 */
export const SNAPSHOT_METRICS: readonly string[] = [
  'cases_active',
  'cases_total',
  'documents_total',
  'tasks_open',
  'tasks_overdue',
  'clients_total',
  'storage_mb',
];

/** Как назвать метрику юристу в настройках. Те же слова, что в Android-версии. */
const LABELS: Record<string, string> = {
  app_opened: 'Запусков',
  case_created: 'Создано дел',
  case_opened: 'Открытий дел',
  document_scanned: 'Сканирований',
  document_from_camera: 'Снимков камерой',
  document_from_gallery: 'Из галереи',
  document_from_files: 'Из файлов',
  document_opened: 'Открытий документов',
  document_renamed: 'Переименований',
  document_typed: 'Проставлено типов',
  document_reordered: 'Изменений порядка',
  document_favorited: 'Отметок «избранное»',
  documents_shared: 'Отправок документов',
  task_created: 'Создано задач',
  task_completed: 'Выполнено задач',
  event_created: 'Создано событий',
  client_created: 'Создано клиентов',
  search_used: 'Поисков',
  search_result_opened: 'Открыто из поиска',
  app_crashed: 'Падений',
  cases_active: 'Активных дел',
  cases_total: 'Всего дел',
  documents_total: 'Всего документов',
  tasks_open: 'Открытых задач',
  tasks_overdue: 'Просроченных задач',
  clients_total: 'Всего клиентов',
  storage_mb: 'Занято, МБ',
};

export const metricLabel = (metric: string): string => LABELS[metric] ?? metric;
